import { useEffect, useState } from "react";
import { acquireDecoded, isHeic, supportsHeicNatively } from "../lib/heicImage";

/**
 * The URL an <img> loads for `src`. Not a HEIC: `src` itself, at once. A HEIC: undefined until the
 * native-support probe answers, then `src` (Safari) or the shared decoded JPEG blob URL (one decode
 * per URL, released when `src` changes or the component unmounts; see lib/heicImage). A failed
 * decode gives `src`, so the <img> shows its normal broken state. `mime` (a file's type) wins over
 * the URL's extension; pass it for local blob: previews, which have none.
 */
export function useImageSrc(src: string | null | undefined, mime?: string | null): string | undefined {
  const heic = !!src && isHeic(src, mime);
  const [shown, setShown] = useState<{ for: string; url: string } | null>(null);

  useEffect(() => {
    if (!heic || !src) return;
    let active = true;
    let release: (() => void) | undefined;
    void supportsHeicNatively().then((native) => {
      if (!active) return;
      if (native) {
        setShown({ for: src, url: src });
        return;
      }
      const ref = acquireDecoded(src);
      release = ref.release;
      void ref.url.then((url) => {
        if (active) setShown({ for: src, url: url ?? src });
      });
    });
    return () => {
      active = false;
      release?.();
    };
  }, [src, heic]);

  if (!src) return undefined;
  if (!heic) return src;
  return shown?.for === src ? shown.url : undefined;
}
